import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Shield, Save, Loader2, Users, User, RotateCcw, Lock,
  CheckCircle, AlertTriangle, Search, FileText,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ─── Page-Access Matrix ───
// Lets the superuser tick/untick every sidebar page for a role or user.
// Groups follow the sidebar's groups so the editor mirrors the UI users see.
function PageAccessMatrix({ pages, groups, access, onChange, readOnly }) {
  // Group pages by their `group` so the UI reads like the sidebar
  const grouped = React.useMemo(() => {
    const out = {};
    pages.forEach((p) => {
      (out[p.group] = out[p.group] || []).push(p);
    });
    return out;
  }, [pages]);

  const toggleGroup = (group, value) => {
    grouped[group].forEach((p) => onChange(p.path, value));
  };

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const groupPages = grouped[group] || [];
        if (groupPages.length === 0) return null;
        const enabledCount = groupPages.filter((p) => access[p.path] !== false).length;
        const allOn = enabledCount === groupPages.length;
        const allOff = enabledCount === 0;
        return (
          <div key={group} className="border border-border rounded-lg overflow-hidden" data-testid={`page-group-${group.toLowerCase()}`}>
            <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{group}</span>
                <Badge variant="outline" className="text-[10px]">{enabledCount}/{groupPages.length}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={readOnly || allOn}
                  onClick={() => toggleGroup(group, true)}
                  data-testid={`page-group-enable-all-${group.toLowerCase()}`}
                >
                  Enable all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs text-rose-600 hover:text-rose-700"
                  disabled={readOnly || allOff}
                  onClick={() => toggleGroup(group, false)}
                  data-testid={`page-group-disable-all-${group.toLowerCase()}`}
                >
                  Disable all
                </Button>
              </div>
            </div>
            <div className="divide-y divide-border/50">
              {groupPages.map((p) => {
                const enabled = access[p.path] !== false;
                return (
                  <label
                    key={p.path}
                    className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer group"
                    data-testid={`page-row-${p.path.replace(/\//g, '-').slice(1) || 'root'}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">{p.label}</span>
                      {p.admin_only && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Admin</Badge>
                      )}
                      <code className="text-[10px] text-muted-foreground font-mono truncate">{p.path}</code>
                    </div>
                    <Switch
                      checked={enabled}
                      disabled={readOnly}
                      onCheckedChange={(v) => onChange(p.path, v)}
                      className="h-4 w-7 data-[state=checked]:bg-emerald-600"
                      data-testid={`page-toggle-${p.path.replace(/\//g, '-').slice(1) || 'root'}`}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Permission Matrix for a single role/user ───
function PermissionMatrix({ permissions, schema, sectionLabels, actionLabels, onChange, readOnly }) {
  const sections = Object.keys(schema);

  return (
    <div className="space-y-1">
      {sections.map((section) => {
        const actions = Object.keys(schema[section]);
        const sectionPerms = permissions[section] || {};

        return (
          <div key={section} className="flex items-center gap-2 py-2 px-3 hover:bg-muted/50 rounded-lg group" data-testid={`perm-section-${section}`}>
            <span className="text-sm font-medium w-40 flex-shrink-0">{sectionLabels[section] || section}</span>
            <div className="flex flex-wrap gap-3 flex-1">
              {actions.map((action) => {
                const enabled = sectionPerms[action] ?? schema[section][action] ?? false;
                return (
                  <label
                    key={action}
                    className="flex items-center gap-1.5 cursor-pointer select-none"
                    data-testid={`perm-${section}-${action}`}
                  >
                    <Switch
                      checked={enabled}
                      disabled={readOnly}
                      onCheckedChange={(val) => onChange(section, action, val)}
                      className="h-4 w-7 data-[state=checked]:bg-emerald-600"
                    />
                    <span className={`text-xs ${enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                      {actionLabels[action] || action}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PermissionsPage() {
  const { user: currentUser, refreshPermissions } = useAuth();
  const [tab, setTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState({});
  const [sectionLabels, setSectionLabels] = useState({});
  const [actionLabels, setActionLabels] = useState({});
  const [rolePerms, setRolePerms] = useState({});
  const [defaults, setDefaults] = useState({});
  const [selectedRole, setSelectedRole] = useState('staff');

  // User overrides
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [userOverride, setUserOverride] = useState({});
  const [userResolved, setUserResolved] = useState({});
  const [userSearch, setUserSearch] = useState('');

  // Page-access state
  const [pageRegistry, setPageRegistry] = useState({ pages: [], groups: [] });
  const [rolePageAccess, setRolePageAccess] = useState({});
  const [userPageAccess, setUserPageAccess] = useState({}); // resolved/effective for selected user
  const [pageAccessTarget, setPageAccessTarget] = useState('role'); // 'role' | 'user'

  const fetchSchema = useCallback(async () => {
    try {
      const { data } = await api.get('/permissions/schema');
      setSchema(data.schema || {});
      setSectionLabels(data.section_labels || {});
      setActionLabels(data.action_labels || {});
    } catch {
      toast.error('Failed to load permission schema');
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await api.get('/permissions/roles');
      setRolePerms(data.roles || {});
      setDefaults(data.defaults || {});
    } catch {
      toast.error('Failed to load role permissions');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/users');
      setAllUsers(data.users || data || []);
    } catch {}
  }, []);

  // Pull the page registry + per-role page access so the Pages tab can render
  const fetchPageRegistry = useCallback(async () => {
    try {
      const [schemaRes, rolesRes] = await Promise.all([
        api.get('/permissions/pages-schema'),
        api.get('/permissions/pages/roles'),
      ]);
      setPageRegistry({
        pages: schemaRes.data?.pages || [],
        groups: schemaRes.data?.groups || [],
      });
      setRolePageAccess(rolesRes.data?.roles || {});
    } catch {
      toast.error('Failed to load page access schema');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSchema(), fetchRoles(), fetchUsers(), fetchPageRegistry()]).finally(() => setLoading(false));
  }, [fetchSchema, fetchRoles, fetchUsers, fetchPageRegistry]);

  const handleRoleChange = (section, action, value) => {
    setRolePerms((prev) => ({
      ...prev,
      [selectedRole]: {
        ...prev[selectedRole],
        [section]: {
          ...(prev[selectedRole]?.[section] || {}),
          [action]: value,
        },
      },
    }));
  };

  const saveRolePermissions = async () => {
    setSaving(true);
    try {
      await api.post('/permissions/roles', {
        role: selectedRole,
        permissions: rolePerms[selectedRole],
      });
      toast.success(`${selectedRole} permissions saved`);
      refreshPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetRoleToDefaults = () => {
    if (!defaults[selectedRole]) return;
    setRolePerms((prev) => ({ ...prev, [selectedRole]: JSON.parse(JSON.stringify(defaults[selectedRole])) }));
    toast.info(`Reset ${selectedRole} to defaults (not saved yet)`);
  };

  // User overrides
  const loadUserPermissions = async (userId) => {
    setSelectedUserId(userId);
    try {
      const [permRes, pageRes] = await Promise.all([
        api.get(`/permissions/users/${userId}`),
        api.get(`/permissions/pages/users/${userId}`),
      ]);
      setSelectedUserInfo(permRes.data.user);
      setUserOverride(permRes.data.override || {});
      setUserResolved(permRes.data.resolved || {});
      setUserPageAccess(pageRes.data.resolved || {});
    } catch {
      toast.error('Failed to load user permissions');
    }
  };

  const handleUserOverrideChange = (section, action, value) => {
    setUserOverride((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [action]: value,
      },
    }));
    // Also update resolved view
    setUserResolved((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [action]: value,
      },
    }));
  };

  const saveUserOverride = async () => {
    setSaving(true);
    try {
      await api.post(`/permissions/users/${selectedUserId}`, { permissions: userOverride });
      toast.success(`Permissions for ${selectedUserInfo?.name} saved`);
      refreshPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clearUserOverride = async () => {
    if (!confirm('Reset to role defaults? This removes all per-user overrides.')) return;
    try {
      await api.delete(`/permissions/users/${selectedUserId}`);
      setUserOverride({});
      await loadUserPermissions(selectedUserId);
      toast.success('User overrides cleared');
    } catch {
      toast.error('Failed to clear overrides');
    }
  };

  // ─── Page-access mutations ───
  const handleRolePageToggle = (path, value) => {
    setRolePageAccess((prev) => ({
      ...prev,
      [selectedRole]: { ...(prev[selectedRole] || {}), [path]: value },
    }));
  };

  const saveRolePageAccess = async () => {
    setSaving(true);
    try {
      await api.post('/permissions/pages/roles', {
        role: selectedRole,
        page_access: rolePageAccess[selectedRole] || {},
      });
      toast.success(`${selectedRole} page access saved`);
      refreshPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save page access');
    } finally {
      setSaving(false);
    }
  };

  const handleUserPageToggle = (path, value) => {
    setUserPageAccess((prev) => ({ ...prev, [path]: value }));
  };

  const saveUserPageAccess = async () => {
    setSaving(true);
    try {
      await api.post(`/permissions/pages/users/${selectedUserId}`, {
        page_access: userPageAccess,
      });
      toast.success(`Page access for ${selectedUserInfo?.name} saved`);
      refreshPermissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clearUserPageAccess = async () => {
    if (!confirm('Reset this user\'s page access to their role defaults?')) return;
    try {
      await api.delete(`/permissions/pages/users/${selectedUserId}`);
      await loadUserPermissions(selectedUserId);
      toast.success('Page overrides cleared');
    } catch {
      toast.error('Failed to clear page overrides');
    }
  };

  const filteredUsers = allUsers.filter((u) =>
    u.role !== 'superuser' &&
    (!userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()))
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="permissions-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Access Control</h1>
            <p className="text-slate-500 text-sm">Manage page and action permissions for each role and individual user</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="roles" data-testid="tab-roles">
              <Users className="w-4 h-4 mr-1" /> Role Permissions
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <User className="w-4 h-4 mr-1" /> User Overrides
            </TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">
              <FileText className="w-4 h-4 mr-1" /> Page Access
            </TabsTrigger>
          </TabsList>

          {/* ─── Role Permissions Tab ─── */}
          <TabsContent value="roles" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">Role Permissions</CardTitle>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger className="w-36 h-9" data-testid="role-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="superuser" disabled>Superuser (Full Access)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={resetRoleToDefaults} data-testid="reset-role-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Defaults
                    </Button>
                    <Button size="sm" onClick={saveRolePermissions} disabled={saving || selectedRole === 'superuser'} data-testid="save-role-btn">
                      {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Save {selectedRole}
                    </Button>
                  </div>
                </div>
                {selectedRole === 'superuser' && (
                  <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                    <Lock className="w-3.5 h-3.5" /> Superuser has full access to everything and cannot be modified.
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[60vh]">
                  <PermissionMatrix
                    permissions={rolePerms[selectedRole] || {}}
                    schema={schema}
                    sectionLabels={sectionLabels}
                    actionLabels={actionLabels}
                    onChange={handleRoleChange}
                    readOnly={selectedRole === 'superuser'}
                  />
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── User Overrides Tab ─── */}
          <TabsContent value="users" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* User list */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Users</CardTitle>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      className="pl-9 h-9 text-sm"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      data-testid="user-perm-search"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="max-h-[55vh]">
                    {filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${selectedUserId === u.id ? 'bg-muted/80 border-l-2 border-l-violet-600' : ''}`}
                        onClick={() => loadUserPermissions(u.id)}
                        data-testid={`user-perm-${u.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px] capitalize">{u.role || 'staff'}</Badge>
                        </div>
                      </button>
                    ))}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* User permissions */}
              <Card className="lg:col-span-2">
                {selectedUserInfo ? (
                  <>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            {selectedUserInfo.name}
                            <Badge variant="outline" className="text-xs capitalize">{selectedUserInfo.role}</Badge>
                          </CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">{selectedUserInfo.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={clearUserOverride} data-testid="clear-user-override">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Role
                          </Button>
                          <Button size="sm" onClick={async () => { await saveUserPageAccess(); await saveUserOverride(); }} disabled={saving} data-testid="save-user-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                            Save All
                          </Button>
                        </div>
                      </div>
                      {Object.keys(userOverride).length > 0 && (
                        <div className="flex items-center gap-2 text-xs text-violet-700 bg-violet-50 rounded-lg px-3 py-2 mt-2">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          This user has custom overrides applied on top of their role defaults.
                        </div>
                      )}
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="max-h-[55vh] pr-2">
                        {/* Feature access — turn any feature on/off for this user */}
                        <div className="mb-5">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-sm font-semibold">Feature access</p>
                              <p className="text-xs text-muted-foreground">Show or hide each feature for this user.</p>
                            </div>
                            <Button size="sm" onClick={saveUserPageAccess} disabled={saving} data-testid="save-user-pages-btn">
                              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                              Save Features
                            </Button>
                          </div>
                          <PageAccessMatrix
                            pages={pageRegistry.pages}
                            groups={pageRegistry.groups}
                            access={userPageAccess}
                            onChange={handleUserPageToggle}
                            readOnly={selectedUserInfo.role === 'superuser'}
                          />
                        </div>

                        {/* Detailed permissions — fine-grained actions per feature */}
                        <div className="flex items-center justify-between mb-2 pt-2 border-t border-border">
                          <div>
                            <p className="text-sm font-semibold">Detailed permissions</p>
                            <p className="text-xs text-muted-foreground">Fine-grained actions (View / Create / Edit / Delete / Generate…).</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={saveUserOverride} disabled={saving} data-testid="save-user-actions-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                            Save Actions
                          </Button>
                        </div>
                        <PermissionMatrix
                          permissions={userResolved}
                          schema={schema}
                          sectionLabels={sectionLabels}
                          actionLabels={actionLabels}
                          onChange={handleUserOverrideChange}
                          readOnly={false}
                        />
                      </ScrollArea>
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-20 text-center">
                    <div>
                      <User className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Select a user to manage their permissions</p>
                      <p className="text-xs text-muted-foreground mt-1">Overrides apply on top of the role's base permissions</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ─── Page Access Tab ─── */}
          <TabsContent value="pages" className="space-y-4 mt-4">
            {/* Sub-target selector: tweak the role default, or override an individual user */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <CardTitle className="text-base">Page Access</CardTitle>
                    <Tabs value={pageAccessTarget} onValueChange={setPageAccessTarget}>
                      <TabsList className="h-9">
                        <TabsTrigger value="role" data-testid="page-target-role">
                          <Users className="w-3.5 h-3.5 mr-1" /> By role
                        </TabsTrigger>
                        <TabsTrigger value="user" data-testid="page-target-user">
                          <User className="w-3.5 h-3.5 mr-1" /> By user
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    {pageAccessTarget === 'role' && (
                      <Select value={selectedRole} onValueChange={setSelectedRole}>
                        <SelectTrigger className="w-36 h-9" data-testid="page-role-selector">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="superuser" disabled>Superuser (Full Access)</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  {pageAccessTarget === 'role' && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={saveRolePageAccess}
                        disabled={saving || selectedRole === 'superuser'}
                        data-testid="save-role-pages-btn"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Save {selectedRole} pages
                      </Button>
                    </div>
                  )}
                </div>
                {pageAccessTarget === 'role' && selectedRole === 'superuser' && (
                  <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                    <Lock className="w-3.5 h-3.5" /> Superuser sees every page — this cannot be changed.
                  </div>
                )}
                {pageAccessTarget === 'role' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Sets the default sidebar pages every <strong>{selectedRole}</strong> sees. Per-user overrides take priority.
                  </p>
                )}
              </CardHeader>
              {pageAccessTarget === 'role' && (
                <CardContent>
                  <ScrollArea className="max-h-[60vh] pr-3">
                    <PageAccessMatrix
                      pages={pageRegistry.pages}
                      groups={pageRegistry.groups}
                      access={rolePageAccess[selectedRole] || {}}
                      onChange={handleRolePageToggle}
                      readOnly={selectedRole === 'superuser'}
                    />
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* Per-user override */}
            {pageAccessTarget === 'user' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Users</CardTitle>
                    <div className="relative mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users..."
                        className="pl-9 h-9 text-sm"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        data-testid="user-page-search"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="max-h-[55vh]">
                      {filteredUsers.map((u) => (
                        <button
                          key={u.id}
                          className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${selectedUserId === u.id ? 'bg-muted/80 border-l-2 border-l-violet-600' : ''}`}
                          onClick={() => loadUserPermissions(u.id)}
                          data-testid={`user-page-${u.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{u.name}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] capitalize">{u.role || 'staff'}</Badge>
                          </div>
                        </button>
                      ))}
                    </ScrollArea>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  {selectedUserInfo ? (
                    <>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              {selectedUserInfo.name}
                              <Badge variant="outline" className="text-xs capitalize">{selectedUserInfo.role}</Badge>
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-1">{selectedUserInfo.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={clearUserPageAccess} data-testid="clear-user-pages">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to role
                            </Button>
                            <Button size="sm" onClick={saveUserPageAccess} disabled={saving} data-testid="save-user-pages-btn">
                              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                              Save pages
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Tick to allow, untick to hide and block. Changes save only when you click <strong>Save pages</strong>.
                        </p>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="max-h-[55vh] pr-3">
                          <PageAccessMatrix
                            pages={pageRegistry.pages}
                            groups={pageRegistry.groups}
                            access={userPageAccess}
                            onChange={handleUserPageToggle}
                            readOnly={false}
                          />
                        </ScrollArea>
                      </CardContent>
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-20 text-center">
                      <div>
                        <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Select a user to manage their page access</p>
                        <p className="text-xs text-muted-foreground mt-1">Their personal override sits on top of the role default.</p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
